import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  StyledBanner,
  StyledButton,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { deletePlace, postPlace, updatePlace } from '../../services/placesService';
import { apiParamsState } from '../../storage/app';
import {
  editingPlaceState,
  editSelectedPlaceState,
  selectedPlaceIdState,
  selectedPlaceState,
  updatePlacesState,
} from '../../storage/places';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import ConfirmModal from '../shared/ConfirmModal';

export function PlaceDetail() {
  const { theme, css } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const { openModal, closeModal, showModal } = useModal();
  const { showDeletionFeedback, showFailFeedback, showPositiveFeedback } = useFeedback();
  const [edit, setEdit] = useRecoilState(editSelectedPlaceState);
  const [selectedPlaceId, setSelectedPlaceId] = useRecoilState(selectedPlaceIdState);
  const apiParams = useRecoilValue(apiParamsState);
  const editingPlace = useRecoilValue(editingPlaceState);
  const isNewPlace = editingPlace?._id === '';
  const canEdit = !isNewPlace;
  const place = useRecoilValue(selectedPlaceState);
  const resetEdit = useResetRecoilState(editSelectedPlaceState);
  const resetEditingPlace = useResetRecoilState(editingPlaceState);
  const resetSelectedPlaceId = useResetRecoilState(selectedPlaceIdState);
  const setUpdatePlaces = useSetRecoilState(updatePlacesState);

  useEffect(() => {
    return () => {
      resetEditingPlace();
      resetEdit();
    };
  }, [selectedPlaceId]);

  useEffect(() => {
    if (isNewPlace) {
      onEdit();
    }
  }, [isNewPlace]);

  if (editingPlace === undefined) return null;

  const canSubmit = !!editingPlace.geoPoint;
  const hasUpdates = !areEqual(place || {}, editingPlace);

  const onDeletePlace = async () => {
    if (!apiParams || selectedPlaceId === undefined) return;
    const { error } = await deletePlace(apiParams.clientId, apiParams.subClientId, selectedPlaceId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.place, place?.uuid));
      setUpdatePlaces('update');
      resetSelectedPlaceId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.place, place?.uuid));
    }
  };

  const onSavePlace = async () => {
    if (!apiParams) return;
    startLoading();
    const { id, error } = isNewPlace
      ? await postPlace(apiParams.clientId, apiParams.subClientId, editingPlace)
      : await updatePlace(apiParams.clientId, apiParams.subClientId, editingPlace);
    if (!error && id) {
      if (isNewPlace) setSelectedPlaceId(id);
      showPositiveFeedback(
        isNewPlace ? FEEDBACK.created(FEEDBACK_PREFIXES.place) : FEEDBACK.edited(FEEDBACK_PREFIXES.place, place?.uuid)
      );
      setUpdatePlaces(id);
      resetEdit();
    } else {
      showFailFeedback(
        error || isNewPlace
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.place)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.place, place?.uuid)
      );
    }
    stopLoading();
  };

  const onEdit = () => {
    setEdit(true);
  };

  const onStopEditing = () => {
    resetEdit();
    resetEditingPlace();
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <HeadingSmall margin={0}>{isNewPlace ? 'Nuevo lugar' : place?.uuid}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1 })}>
        {place?.uuid && (
          <LabeledInput
            label="UUID:"
            value={place.uuid}
            disabled
          />
        )}
        <LabeledInput
          label="Dirección:"
          value={editingPlace.address}
          disabled
        />
        {edit && (
          <StyledBanner>Haz click en el mapa o arrastra el marcador para seleccionar la ubicación.</StyledBanner>
        )}
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewPlace ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewPlace && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={() => {
              if (canEdit && edit) {
                onStopEditing();
              } else {
                resetSelectedPlaceId();
              }
            }}
          >
            {canEdit && edit ? 'Cancelar' : 'Cerrar'}
          </StyledButton>
          {edit && (
            <StyledButton
              onClick={onSavePlace}
              isLoading={loading}
              disabled={!canSubmit || !hasUpdates}
            >
              Guardar
            </StyledButton>
          )}
          {!edit && (
            <StyledButton
              onClick={onEdit}
              kind="tertiary"
            >
              Editar
            </StyledButton>
          )}
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeletePlace}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{place?.uuid}"?</ParagraphMedium>}
      />
    </FloatingCardContent>
  );
}
